import { useNotification } from "@context/NotificationContext";
import type { DisputeDetails, ReviewDisputeResolutionPayload } from "@services/disputes/types";
import { updateDispute } from "@services/disputesApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const SERVICE = "bff-vessel-call";
const LANG_BASE = "vessel-call.operations";

type ReviewResolutionVariables = {
  disputeId: string;
  payload: ReviewDisputeResolutionPayload;
};

export const useReviewDisputeResolution = () => {
  const { notify } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation<DisputeDetails, unknown, ReviewResolutionVariables>({
    mutationFn: ({ disputeId, payload }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "Operations",
          action:
            payload.action === "ACCEPT" ? "accept_dispute_resolution" : "reject_dispute_resolution",
          entityType: "dispute",
          entityId: disputeId,
        },
        (transactionId) => updateDispute({ service: SERVICE, disputeId, payload, transactionId }),
      ),
    onSuccess: async (updatedDispute, variables) => {
      queryClient.setQueryData(["dispute", SERVICE, variables.disputeId], updatedDispute);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["disputes"] }),
        queryClient.invalidateQueries({ queryKey: ["dispute"] }),
        queryClient.invalidateQueries({ queryKey: ["vesselCall"] }),
      ]);

      notify({
        title: t(
          `${LANG_BASE}.${
            variables.payload.action === "ACCEPT"
              ? "accept-resolution-success-title"
              : "reject-resolution-success-title"
          }`,
        ),
        type: "success",
      });
    },
    onError: () => {
      notify({
        title: t("common.error"),
        description: t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  return {
    reviewResolution: mutation.mutate,
    isReviewingResolution: mutation.isPending,
  };
};
